// Auth queries and the OAuth-start helper. `useUser` is the source of truth
// for "am I signed in" — we hand its result to AuthProvider which exposes it
// through router context so route guards can decide where to send the user.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { normalizeLocalReturnTo, routes, type User } from "@nova-ai/shared"

import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"

export function useUser() {
  return useQuery<User | null>({
    queryKey: queryKeys.authUser,
    queryFn: async () => {
      const result = await apiFetch<{ user: User }>(routes.auth.me())
      if (!result.ok) {
        // 401 just means "not signed in"; surface it as a null user
        // rather than a thrown error so the auth context stays usable.
        if (result.error.kind === "unauthorized") {
          return null
        }
        throw new Error(result.error.message)
      }
      return result.value.user
    },
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  })
}

export function initiateGoogleLogin(redirectTo = "/dashboard"): void {
  const search = new URLSearchParams({
    redirect: normalizeLocalReturnTo(redirectTo, "/dashboard"),
  })
  window.location.href = `/auth/google/start?${search.toString()}`
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const result = await apiFetch("/logout", { method: "POST" })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
    },
    onSuccess: () => {
      queryClient.setQueryData(queryKeys.authUser, null)
    },
  })
}
